import { StethoscopeIcon } from "assets/icons/Stethoscope";
import { AccountFeeType } from "constants/fee";
import { isElementDuplicated } from "helpers/array";
import {
  calculateDiscount,
  calculateDiscountPercent,
  calculateProviderFinalPayment,
} from "helpers/price";
import { roundToX } from "helpers/round";
import { useGetAccountDiscountAndFee } from "hooks/useGetAccountDiscountAndFee";
import { useQuery } from "hooks/useQuery";
import { sizer } from "layout/styles/styled/sizer";
import { StyledFieldGrid } from "pages/CreateOrder/CreateOrder.styled";
import { AutoCompleteSelectParams } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useGetAccountByIdQuery } from "store/queries/account";
import { useGetBillingCodesQuery } from "store/queries/billingCode";
import { TourAnchor } from "training/anchors";
import { IBillingCode } from "types/BillingCode";

import { StepCard } from "../../../StepCard";
import { StepKey } from "../../../StepCard/helper";
import { useStepStatus } from "../../../StepCard/useStepStatus";
import { CptCodePill } from "../Common/CptCodePill";
import { NoServices } from "../Common/NoServices";
import { ServicesFooter } from "../Common/ServicesFooter";
import {
  StyledAutoComplete,
  StyledServicesTable,
  StyledSuggestionRow,
  StyledTrashButton,
} from "../Common/styled";

export type ServiceDetails = {
  amount: any;
  totalAmount: any;
  serviceDetails: Array<{ code: string; cptDescription: string }>;
  providerFinalPayment: any;
  totalInsuranceAllowable: any;
  clearPatientFee?: number | null;
  clearPatientFeePercentage?: number | null;
  isDiscountLoading?: boolean;
  discountPercent?: number | null;
};

export function PatientResponsibilityServiceDetails({
  selectedProceduresList,
  redo,
  draft,
  copy,
  isFirstRender,
}: {
  selectedProceduresList?: string[];
  redo?: boolean;
  draft?: boolean;
  copy?: boolean;
  isFirstRender?: boolean;
}) {
  const query = useQuery();
  const accountId = query.get("accountId");
  const { setValue, control, watch } = useFormContext<
    ServiceDetails & {
      initialClearFeeValueInPercentage?: number | null;
    }
  >();
  const initialClearFeeValueInPercentage = watch(
    "initialClearFeeValueInPercentage" as any
  );

  const status = useStepStatus(StepKey.SERVICES);
  const { data: billingCodesFromAPI } = useGetBillingCodesQuery();
  const { data: accountData, isLoading: isAccountDataLoading } =
    useGetAccountByIdQuery(accountId || "");
  const billingCodes = billingCodesFromAPI?.data || [];

  const [selectedBillingCode, setSelectedBillingCode] = useState<string[]>(
    selectedProceduresList || []
  );
  const [filteredBillingCodes, setFilteredBillingCodes] = useState<
    IBillingCode[]
  >([]);

  const parsedFilteredBillingCodes = useMemo(() => {
    return filteredBillingCodes.map(
      (billingCode: IBillingCode) =>
        `${billingCode.name} - ${billingCode.description}`
    );
  }, [filteredBillingCodes]);

  const parsedSelectedBillingCodesToTable = useMemo(() => {
    if (!selectedBillingCode) return [];

    return selectedBillingCode.map((selectedBillingCode) => {
      const billingCodeName = selectedBillingCode.split("-")[0];
      const billingCodeDescription = selectedBillingCode
        .split("-")
        .slice(1)
        .join("-");

      return {
        code: billingCodeName,
        cptDescription: billingCodeDescription,
      };
    });
  }, [selectedBillingCode]);

  const search = (event: { query: string }) => {
    let filteredBillingCodesToSet;

    if (!event.query.trim().length) {
      filteredBillingCodesToSet = [...billingCodes];
    } else {
      filteredBillingCodesToSet = billingCodes.filter(
        (billingCode: IBillingCode) => {
          return `${billingCode.name} - ${billingCode.description}`
            .toLowerCase()
            .includes(event.query.toLowerCase());
        }
      );
    }

    setFilteredBillingCodes(filteredBillingCodesToSet);
  };

  const handleOnSelectBillingCode = (e: AutoCompleteSelectParams) => {
    const billingCodeAlreadySelected = selectedBillingCode.find(
      (currentBillingCode) => currentBillingCode === e.value
    );

    if (billingCodeAlreadySelected) {
      setSelectedBillingCode((prevState) => [...prevState, e.value]);
    }
  };

  const handleOnUnSelectBillingCode = (e: AutoCompleteSelectParams) => {
    const isDuplicated = isElementDuplicated(selectedBillingCode, e.value);

    if (!isDuplicated) return;

    const billingCodeIndex = selectedBillingCode.findIndex(
      (currentBillingCode) => currentBillingCode === e.value
    );

    if (billingCodeIndex !== -1) {
      const newSelectedBillingCode = selectedBillingCode.splice(
        billingCodeIndex,
        1
      );

      setSelectedBillingCode(newSelectedBillingCode);
    }
  };

  const renderSuggestion = useCallback((suggestion: string) => {
    const code = suggestion.split("-")[0].trim();
    const description = suggestion.split("-").slice(1).join("-").trim();

    return (
      <StyledSuggestionRow>
        <span className="flex-shrink-0">
          <CptCodePill code={code} />
        </span>

        <span
          className="flex-1 text-sm font-semibold line-height-2"
          style={{ color: "var(--brand-black)", minWidth: 0 }}
        >
          {description}
        </span>
      </StyledSuggestionRow>
    );
  }, []);

  const cptCodeTemplate = (row: { code: string }) => (
    <CptCodePill code={row.code.trim()} />
  );

  // The table rows map 1:1 onto selectedBillingCode, so the row index addresses
  // the entry to drop.
  const removeBillingCodeAt = (rowIndex: number) =>
    setSelectedBillingCode((prevState) =>
      prevState.filter((_, index) => index !== rowIndex)
    );

  const actionTemplate = (
    _row: unknown,
    { rowIndex }: { rowIndex: number }
  ) => (
    <StyledTrashButton
      aria-label="Remove service"
      icon="pi pi-trash"
      className="p-button-text p-button-rounded"
      onClick={() => removeBillingCodeAt(rowIndex)}
    />
  );

  const { accountDiscount, isLoading: isDiscountLoading } =
    useGetAccountDiscountAndFee(AccountFeeType.PRDiscount);

  const [customDiscountPercent, setCustomDiscountPercent] = useState<
    number | null
  >(null);

  const [isEditMode, setEditMode] = useState(false);

  const totalAmount = watch("totalAmount");
  const amount = watch("amount");

  const discount = useMemo(() => {
    if (
      isEditMode ||
      customDiscountPercent ||
      (redo && isFirstRender) ||
      (draft && isFirstRender) ||
      (copy && isFirstRender)
    ) {
      const newDiscount = calculateDiscountPercent(totalAmount, amount);
      let discountRounded = roundToX(newDiscount);
      if (Number.isNaN(discountRounded)) {
        discountRounded = 0;
      }

      setCustomDiscountPercent(discountRounded);

      return discountRounded;
    }

    return accountDiscount;
  }, [accountDiscount, isEditMode, amount]);

  const hideClearPrice = !Number(discount) && !isEditMode;

  useEffect(() => {
    setValue("serviceDetails", parsedSelectedBillingCodesToTable);
  }, [parsedSelectedBillingCodesToTable]);

  useEffect(() => {
    setValue("isDiscountLoading", isDiscountLoading || isAccountDataLoading);
  }, [isDiscountLoading, isAccountDataLoading]);

  useEffect(() => {
    setValue("discountPercent", discount);
  }, [discount]);

  useEffect(
    () => () => {
      setValue("isDiscountLoading", false);
      setValue("discountPercent", null);
    },
    []
  );

  // this type of orders do not have clearPatientFee and clearPatientFeePercentage, so in case user changes order type, we always reset these values to null
  useEffect(() => {
    setValue("clearPatientFee", null);
    setValue("clearPatientFeePercentage", null);
  }, []);

  useEffect(() => {
    if (!totalAmount && isEditMode) return;
    // For redo/draft: ALWAYS use the order's saved value from the order table.
    // If the order has no clearFee (null or 0): use 0, do NOT fall back to account
    // For initial creation/copy: look up from account table
    let clearFeeValueInPercentage;
    if (redo || draft) {
      clearFeeValueInPercentage = initialClearFeeValueInPercentage ?? 0;
    } else {
      clearFeeValueInPercentage =
        accountData?.data?.accountFees?.length &&
        accountData.data.accountFees.find(
          (fee: any) => fee.type === AccountFeeType.CTFForPR
        )?.valueInPercentage;
    }
    if (!isEditMode) {
      setValue("amount", calculateDiscount(totalAmount, discount));
    }
    setValue(
      "providerFinalPayment",
      calculateProviderFinalPayment(amount, clearFeeValueInPercentage)
    );
  }, [
    totalAmount,
    isEditMode,
    accountData,
    amount,
    redo,
    draft,
    initialClearFeeValueInPercentage,
  ]);

  return (
    <StepCard
      status={status}
      icon={<StethoscopeIcon width={20} height={20} />}
      title="Services"
      description="Add CPT codes to build the charge estimate"
      data-testid="serviceDetails_PatientResponsibilityServiceDetails"
    >
      {!(isDiscountLoading || isAccountDataLoading) && (
        <StyledFieldGrid
          data-tour={TourAnchor.PatientBalanceAmount}
          className="p-3 mb-4"
          style={{
            borderRadius: "12px",
            background:
              "color-mix(in srgb, var(--brand-gray-50) 40%, var(--brand-white))",
            border: "1px solid var(--brand-gray-200)",
          }}
        >
          <div className="grid">
            <div className="col-12 md:col-6 flex flex-column gap-2">
              <label htmlFor="totalAmountInput">Patient Balance</label>
              <Controller
                name="totalAmount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputNumber
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                    value={value}
                    className="w-full"
                    inputStyle={{
                      background: "var(--brand-gray-50)",
                      borderColor:
                        "color-mix(in srgb, var(--brand-purple-700) 50%, var(--brand-white))",
                    }}
                    inputId="totalAmountInput"
                    onChange={({ value: next }) => onChange(next)}
                  />
                )}
              />
            </div>

            <div
              className={`col-12 md:col-6 flex-column gap-2 ${
                hideClearPrice ? "hidden" : "flex"
              }`}
            >
              <label htmlFor="amountInput">Clear Price for Patient</label>
              <div className="flex align-items-center">
                <Controller
                  control={control}
                  name="amount"
                  render={({ field: { onChange, value } }) => (
                    <InputNumber
                      value={value}
                      currency="USD"
                      locale="en-US"
                      mode="currency"
                      className="w-full"
                      inputStyle={{
                        background: "var(--brand-gray-50)",
                        borderColor:
                          "color-mix(in srgb, var(--brand-purple-700) 50%, var(--brand-white))",
                      }}
                      inputId="amountInput"
                      disabled={!isEditMode}
                      onChange={({ value: next }) => onChange(next)}
                    />
                  )}
                />
                <Button
                  type="button"
                  disabled={!totalAmount}
                  className="p-button-action p-button-neutral ml-2"
                  label={!isEditMode ? "Edit" : "Save"}
                  onClick={() => setEditMode((prevState) => !prevState)}
                />
              </div>
            </div>
          </div>

          {Boolean(totalAmount) && !hideClearPrice && (
            <p
              className="text-sm mt-2 mb-0"
              style={{ color: "var(--brand-gray-600)" }}
            >
              {Boolean(customDiscountPercent) && (
                <span>Updated with entered price: </span>
              )}
              ({discount}% discount)
            </p>
          )}
        </StyledFieldGrid>
      )}

      <div className="formgrid grid w-100">
        <div className="field p-fluid col-12">
          <label
            htmlFor="code"
            className="text-sm font-semibold mb-2"
            style={{ color: "var(--brand-black)" }}
          >
            Add CPT services
          </label>
          <StyledAutoComplete
            multiple
            id="code"
            className="w-full"
            completeMethod={search}
            data-testid="autocomplete"
            value={selectedBillingCode}
            placeholder="Select CPT Code..."
            suggestions={parsedFilteredBillingCodes}
            itemTemplate={renderSuggestion}
            panelStyle={{
              // PrimeReact sets minWidth to the input width; width 0 lets that win
              // so a long description cannot stretch the panel past the input.
              width: 0,
              padding: 0,
              overflow: "hidden",
              borderRadius: "16px",
              border: "1px solid var(--brand-gray-200)",
              boxShadow:
                "0 4px 6px color-mix(in srgb, var(--brand-black) 5%, transparent), 0 12px 28px color-mix(in srgb, var(--brand-black) 12%, transparent)",
            }}
            onChange={(e) => setSelectedBillingCode(e.value)}
            onSelect={(e) => handleOnSelectBillingCode(e)}
            onUnselect={(e) => handleOnUnSelectBillingCode(e)}
          />
        </div>
      </div>

      {parsedSelectedBillingCodesToTable.length ? (
        <StyledServicesTable>
          <DataTable
            stripedRows
            responsiveLayout="scroll"
            value={parsedSelectedBillingCodesToTable}
          >
            <Column
              field="code"
              header="CPT Code"
              headerStyle={{ width: "110px" }}
              style={{ paddingLeft: sizer(6) }}
              body={(row) => cptCodeTemplate(row)}
            />
            <Column field="cptDescription" header="Description" />
            <Column
              style={{ width: "56px", textAlign: "right" }}
              body={(row, options) => actionTemplate(row, options)}
            />
          </DataTable>

          <ServicesFooter rows={parsedSelectedBillingCodesToTable} />
        </StyledServicesTable>
      ) : (
        <NoServices />
      )}
    </StepCard>
  );
}
